import { API_BASE } from '@/shared/services/api';
/**
 * Calendar API Service
 * Handles all calendar-related API calls
 */
import axios from 'axios';


// Create axios instance with auth header
const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: `${API_BASE}/api/calendar`,
});

api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

// ============ CALENDARS ============

export const getCalendars = async (params = {}) => {
  const response = await api.get('/calendars', { params });
  return response.data;
};

export const getCalendarById = async (id) => {
  const response = await api.get(`/calendars/${id}`);
  return response.data;
};

export const createCalendar = async (data) => {
  const response = await api.post('/calendars', data);
  return response.data;
};

export const updateCalendar = async (id, data) => {
  const response = await api.put(`/calendars/${id}`, data);
  return response.data;
};

export const deleteCalendar = async (id) => {
  const response = await api.delete(`/calendars/${id}`);
  return response.data;
};

// ============ EVENTS ============

export const getEvents = async (calendarIds, start, end, params = {}) => {
  const response = await api.get('/events', {
    params: {
      calendar_ids: calendarIds.join(','),
      start: start.toISOString(),
      end: end.toISOString(),
      ...params,
    },
  });
  return response.data;
};

export const getEventById = async (id) => {
  const response = await api.get(`/events/${id}`);
  return response.data;
};

export const createEvent = async (data) => {
  const response = await api.post('/events', data);
  return response.data;
};

export const updateEvent = async (id, data) => {
  const response = await api.put(`/events/${id}`, data);
  return response.data;
};

export const deleteEvent = async (id) => {
  const response = await api.delete(`/events/${id}`);
  return response.data;
};

// ============ ATTENDEES ============

export const addAttendee = async (eventId, data) => {
  const response = await api.post(`/events/${eventId}/attendees`, data);
  return response.data;
};

export const updateAttendeeResponse = async (eventId, attendeeId, data) => {
  const response = await api.put(`/events/${eventId}/attendees/${attendeeId}/response`, data);
  return response.data;
};

// ============ VIEWS ============

export const getMyDayView = async (date = null) => {
  const params = date ? { date: date.toISOString() } : {};
  const response = await api.get('/views/my-day', { params });
  return response.data;
};

export const getAgendaView = async (calendarIds, start, end, params = {}) => {
  const response = await api.get('/views/agenda', {
    params: {
      calendar_ids: calendarIds.join(','),
      start: start.toISOString(),
      end: end.toISOString(),
      ...params,
    },
  });
  return response.data;
};

// ============ RULES ============

export const getRules = async (params = {}) => {
  const response = await api.get('/rules', { params });
  return response.data;
};

export const getRuleById = async (id) => {
  const response = await api.get(`/rules/${id}`);
  return response.data;
};

export const createRule = async (data) => {
  const response = await api.post('/rules', data);
  return response.data;
};

export const updateRule = async (id, data) => {
  const response = await api.put(`/rules/${id}`, data);
  return response.data;
};

export const testRule = async (id, sampleData) => {
  const response = await api.post(`/rules/${id}/test`, { sample_event_data: sampleData });
  return response.data;
};

// ============ HOLIDAYS ============

export const getHolidayCalendars = async (year = null) => {
  const params = year ? { year } : {};
  const response = await api.get('/config/holiday-calendars', { params });
  return response.data;
};

export const createHolidayCalendar = async (data) => {
  const response = await api.post('/config/holiday-calendars', data);
  return response.data;
};

export const addHoliday = async (calendarId, data) => {
  const response = await api.post(`/config/holiday-calendars/${calendarId}/holidays`, data);
  return response.data;
};

// ============ SYNC ============

export const getSyncConnections = async () => {
  const response = await api.get('/sync/connections');
  return response.data;
};

export const initiateGoogleAuth = async () => {
  const response = await api.post('/sync/connections/google/auth');
  return response.data;
};

export const initiateMicrosoftAuth = async () => {
  const response = await api.post('/sync/connections/microsoft/auth');
  return response.data;
};

export const updateSyncConnection = async (id, data) => {
  const response = await api.put(`/sync/connections/${id}`, data);
  return response.data;
};

export const deleteSyncConnection = async (id) => {
  const response = await api.delete(`/sync/connections/${id}`);
  return response.data;
};

export const triggerSync = async (id) => {
  const response = await api.post(`/sync/connections/${id}/sync-now`);
  return response.data;
};

// ============ SEED ============

export const seedCalendarData = async () => {
  const response = await api.post('/seed');
  return response.data;
};

export default {
  getCalendars,
  getCalendarById,
  createCalendar,
  updateCalendar,
  deleteCalendar,
  getEvents,
  getEventById,
  createEvent,
  updateEvent,
  deleteEvent,
  addAttendee,
  updateAttendeeResponse,
  getMyDayView,
  getAgendaView,
  getRules,
  getRuleById,
  createRule,
  updateRule,
  testRule,
  getHolidayCalendars,
  createHolidayCalendar,
  addHoliday,
  getSyncConnections,
  initiateGoogleAuth,
  initiateMicrosoftAuth,
  updateSyncConnection,
  deleteSyncConnection,
  triggerSync,
  seedCalendarData,
};
